import { Trans, t } from '@lingui/macro'
import { useMemo } from 'react'
import { Line } from 'react-chartjs-2'
import { Paper, Typography, Box } from '@mui/material'
import { EntityEvolutionStatistics } from '@/core/api/statistics'
import { ChartSkeleton, getDefaultLineChartOptions, formatChartDateLabels, createLineDataset } from '@/features/statistics'
import { STATISTICS_CONSTANTS } from '@/features/statistics/lib/constants'

type EntityEvolutionComponentProps = {
    data: EntityEvolutionStatistics | undefined
    isLoading: boolean
}

export function EntityEvolutionComponent({ data, isLoading }: EntityEvolutionComponentProps) {
    const consultationsLabel = t`Consultations`
    const expertisesLabel = t`Expertises`
    const patientsLabel = t`Patients`

    const chartData = useMemo(() => {
        if (!data) return null

        // Merge all days from all entity types
        const allDays = new Set([
            ...data.consultations.map((d) => d.day),
            ...data.expertises.map((d) => d.day),
            ...data.patients.map((d) => d.day),
        ])
        const sortedDays = Array.from(allDays).sort()

        // Map cumulative counts to days (use 0 for days without data, carrying forward last known value)
        const getCountsForDays = (items: { day: string; cumulative: number }[]) => {
            let lastCumulative = 0
            return sortedDays.map((day) => {
                const item = items.find((d) => d.day === day)
                if (item) {
                    lastCumulative = item.cumulative
                }
                return lastCumulative
            })
        }

        const consultationCounts = getCountsForDays(data.consultations)
        const expertiseCounts = getCountsForDays(data.expertises)
        const patientCounts = getCountsForDays(data.patients)

        const formattedLabels = formatChartDateLabels(sortedDays)

        return {
            labels: formattedLabels,
            datasets: [
                createLineDataset(consultationsLabel, consultationCounts, 'primary', 'primaryBackground'),
                createLineDataset(expertisesLabel, expertiseCounts, 'secondary', 'secondaryBackground'),
                createLineDataset(patientsLabel, patientCounts, 'success', 'successBackground'),
            ],
        }
    }, [data, consultationsLabel, expertisesLabel, patientsLabel])

    if (isLoading) {
        return <ChartSkeleton />
    }

    if (!chartData || chartData.labels.length === 0) {
        return (
            <Paper sx={{ p: 3 }}>
                <Typography variant="body1" color="text.secondary">
                    <Trans>No data available</Trans>
                </Typography>
            </Paper>
        )
    }

    return (
        <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
                <Trans>Entity counts over time</Trans>
            </Typography>
            <Box sx={{ height: STATISTICS_CONSTANTS.DEFAULT_CHART_HEIGHT }}>
                <Line data={chartData} options={getDefaultLineChartOptions()} />
            </Box>
        </Paper>
    )
}
